import { useCallback, useEffect, useRef, useState } from "react";
import { LatestPunchResponse, type TimeclockPunch } from "@eagledispatch/shared";
import { apiFetch } from "./api";

export type ToolbarState = "NO_SHIFT" | "WORKING" | "ON_LUNCH";

/** Derives presentation state from the server's latest self-scoped punch. */
export function deriveToolbarState(
  latest: Pick<TimeclockPunch, "punchType"> | null | undefined,
): ToolbarState {
  if (!latest) return "NO_SHIFT";
  if (latest.punchType === "IN" || latest.punchType === "LUNCH_IN") {
    return "WORKING";
  }
  if (latest.punchType === "LUNCH_OUT") return "ON_LUNCH";
  return "NO_SHIFT";
}

export interface PunchState {
  state: ToolbarState;
  latest: TimeclockPunch | null;
  /** SPEC-65: the day's lunch allowance is used (server-derived hint). */
  lunchTakenToday: boolean;
  /**
   * SINGLE-SCAN-MORNINGS (B): false until the first /timeclock/latest
   * resolves. While false, `state` is the initial NO_SHIFT default, NOT a
   * server answer — consumers must treat the shift state as UNKNOWN and fail
   * toward Waiting (decideLanding), never toward the punch screen.
   */
  hydrated: boolean;
  refresh: () => Promise<void>;
}

/**
 * SINGLE-SCAN-MORNINGS (B) — the post-login landing decision. An on-shift
 * driver (WORKING or ON_LUNCH) lands on Waiting and NEVER sees the punch
 * screen; an unknown shift (the /timeclock/latest fetch still in flight or
 * failed before ever succeeding) must fail toward Waiting, never toward the
 * punch screen: showing Clock In to a driver whose IN may have already
 * landed is the 2026-09-20 incident (4 scans + a spurious Clock-In screen
 * mid-shift). Pure so the no-DOM test harness can pin it behaviourally.
 */
export function decideLanding(
  state: ToolbarState,
  hydrated: boolean,
): "waiting" | "punch" {
  if (!hydrated) return "waiting";
  return state === "NO_SHIFT" ? "punch" : "waiting";
}

export interface PunchStateSnapshot {
  state: ToolbarState;
  latest: TimeclockPunch | null;
  hydrated: boolean;
}

/** Pure refresh reducer: initial failures fail closed; later failures keep state. */
export function reducePunchState(
  previous: PunchStateSnapshot,
  result: { punch: TimeclockPunch | null } | null,
): PunchStateSnapshot {
  if (result === null) return previous.hydrated ? previous : { ...previous, state: "NO_SHIFT" };
  return {
    latest: result.punch,
    state: deriveToolbarState(result.punch),
    hydrated: true,
  };
}

/** Hydrates punch state and preserves the last known state across refetch errors. */
export function usePunchState(): PunchState {
  const [latest, setLatest] = useState<TimeclockPunch | null>(null);
  const [state, setState] = useState<ToolbarState>("NO_SHIFT");
  const [lunchTakenToday, setLunchTakenToday] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const mounted = useRef(true);
  const hydratedRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const parsed = LatestPunchResponse.safeParse(
        await apiFetch<unknown>("/api/v1/timeclock/latest"),
      );
      if (!mounted.current) return;
      if (!parsed.success) throw new Error("schema mismatch");
      setLatest(parsed.data.punch);
      setState(deriveToolbarState(parsed.data.punch));
      setLunchTakenToday(parsed.data.lunchTakenToday);
      hydratedRef.current = true;
      setHydrated(true);
    } catch {
      // Initial failure is fail-closed; later failures retain the last known
      // state AND the last known lunch flag (SPEC-65 invariant 7).
      if (!mounted.current || hydratedRef.current) return;
      setLatest(null);
      setState("NO_SHIFT");
      setLunchTakenToday(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void refresh();
    return () => {
      mounted.current = false;
    };
  }, [refresh]);

  return { state, latest, lunchTakenToday, hydrated, refresh };
}

